import React, { Fragment } from "react";

import "../styles/globals.css";
import type { AppProps as AProps } from "next/app";
import { signIn } from "next-auth/react";
import Head from "next/head";
import { DEFAULT_PAGES_REQUIRE_AUTH } from "../config/global.config";
import { ToastProvider } from "../context/toastContext/toastContext";
import useAuthSession from "../lib/auth/hooks/useAuthSession";
import { AuthProvider } from "../lib/auth/context/authContext";
import FullScreenSpinner from "../components/common/Spinner/FullScreenSpinner/FullScreenSpinner";
import GoogleAnalytics from "../components/GoogleAnalytics/GoogleAnalytics";

import siteMetaData from "../config/siteMetaData";

interface AuthProps {
    children: React.ReactNode;
}

/** We've allowed Page components to export requiresAuth so we extend the default here and apply it. */
interface AppProps extends AProps {
    Component: AProps["Component"] & { requiresAuth?: boolean };
}

const Auth = ({ children }: AuthProps) => {
    const { status } = useAuthSession({
        required: true,
        redirectToLogin() {
            signIn("vergo-api", { callbackUrl: process.env.NEXT_PUBLIC_SITE_URL });
        },
        onUnauthenticated() {
            signIn("vergo-api", { callbackUrl: process.env.NEXT_PUBLIC_SITE_URL });
        },
    });

    if (status === "unauthenticated" || status === "loading") {
        return <FullScreenSpinner />;
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <Fragment>{children}</Fragment>;
};

const App = ({ Component, pageProps: { ...pageProps } }: AppProps) => {
    const requiresAuth = typeof Component.requiresAuth === "undefined" ? DEFAULT_PAGES_REQUIRE_AUTH : Component.requiresAuth;

    return (
        <Fragment>
            <Head>
                <title>{siteMetaData.title}</title>
                <meta name="description" content={siteMetaData.description} />
            </Head>
            <AuthProvider>
                <GoogleAnalytics />
                <ToastProvider>
                    {requiresAuth ? (
                        <Auth>
                            <Component {...pageProps} />
                        </Auth>
                    ) : (
                        <Component {...pageProps} />
                    )}
                </ToastProvider>
            </AuthProvider>
        </Fragment>
    );
};

export default App;
